/**
 * AudioActivityIndicator · v2.7.65+E219
 * ─────────────────────────────────────
 * Persistent master audio control for the Admin Console. Sits as a small
 * floating pill (bottom-left). It stays dim/idle when nothing is playing and
 * pulses red the instant ANY governed system audio starts (standard chimes,
 * booking klaxon, panic/SOS loop, Ashley TTS, the test panel). Clicking it at
 * any time immediately stops ALL active audio and returns to the idle state.
 *
 * Purely an audio control — it does not touch booking, navigation, alert
 * logic, or any other console feature.
 */
import { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { subscribe, stopAll } from "@/lib/audioManager";

export default function AudioActivityIndicator() {
  const [playing, setPlaying] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => subscribe((p, c) => { setPlaying(p); setCount(c); }), []);

  const handleStop = () => {
    stopAll();
    // Also silence the two persistent alert loops via their existing ack path
    // so a capped loop element cannot re-arm; this does not alter alert state
    // beyond stopping the sound the dispatcher just acknowledged.
    try { window.dispatchEvent(new CustomEvent("rtg:audio_master_stopped")); } catch { /* noop */ }
  };

  return (
    <button
      type="button"
      onClick={handleStop}
      data-testid="audio-activity-indicator"
      data-audio-active={playing ? "true" : "false"}
      aria-label={playing ? `Stop all audio (${count} playing)` : "Audio idle"}
      title={playing ? "Audio playing — click to stop all" : "No audio playing"}
      className={`fixed bottom-4 left-4 z-[190] flex items-center gap-2 rounded-full border shadow-lg
        transition-all duration-200 select-none cursor-pointer
        ${playing
          ? "px-3 py-2 bg-rose-600 border-rose-300 text-white ring-2 ring-rose-400/60 animate-pulse"
          : "h-9 w-9 justify-center bg-zinc-900/70 border-zinc-700/70 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 backdrop-blur opacity-70 hover:opacity-100"}`}
    >
      {playing
        ? <Volume2 className="h-4 w-4" data-testid="audio-indicator-active-icon" />
        : <VolumeX className="h-4 w-4" data-testid="audio-indicator-idle-icon" />}
      {playing && (
        <span className="text-xs font-bold uppercase tracking-wider">Stop Audio</span>
      )}
      {playing && count > 1 && (
        <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px]" data-testid="audio-indicator-count">{count}</span>
      )}
    </button>
  );
}
